import { useRollbackUniversityImport } from '@actions';
import Button from '@components/V4/Button';
import Modal from '@components/V4/Modal/Modal';
import Select from '@components/V4/Select/Select';
import apiClient from '@contexts/AuthContext/apiClient';
import { universityApis } from '@shared/apis';
import {
  UniversityRankingsImportResultDTO,
  UniversityRankingsImportRowDTO,
} from '@shared/clientTypes/DTOs/UniversityDTO';
import useToast from 'apps/agora/src/hooks/useToast';
import { mergeClassNames } from 'apps/agora/src/utils/helpers';
import moment from 'moment';
import { useRef, useState } from 'react';
import { useQueryClient } from 'react-query';

interface ImportRankingsModalProps {
  defaultYear?: number;
  onClose: () => void;
}

const CURRENT_YEAR = moment().year();
const YEAR_OPTIONS = Array.from({ length: CURRENT_YEAR + 2 - 2015 }, (_, index) => {
  const year = CURRENT_YEAR + 1 - index;
  return { label: String(year), value: year };
});

const errorMessage = (error: unknown, fallback: string) =>
  (error as { response?: { data?: { message?: string; error?: { message?: string } } } })?.response
    ?.data?.message ??
  (error as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
    ?.message ??
  fallback;

const Stat = (props: { label: string; value: string | number; tone?: 'gold' | 'red' }) => (
  <div className="flex flex-col rounded-xl bg-white/5 px-4 py-3">
    <span
      className={mergeClassNames(
        'text-lg font-semibold',
        props.tone === 'gold' && 'text-customGold',
        props.tone === 'red' && 'text-customRed',
        !props.tone && 'text-white'
      )}
    >
      {props.value}
    </span>
    <span className="text-xs text-customGrey">{props.label}</span>
  </div>
);

const RowList = (props: {
  title: string;
  rows: UniversityRankingsImportRowDTO[];
  total: number;
}) => {
  const [open, setOpen] = useState(false);
  if (!props.total) return null;
  return (
    <div className="flex flex-col gap-2 rounded-xl bg-white/5 p-4">
      <button
        type="button"
        className="text-left text-sm font-semibold text-white"
        onClick={() => setOpen((value) => !value)}
      >
        {open ? '▾' : '▸'} {props.title} ({props.total})
      </button>
      {open ? (
        <ul className="max-h-64 overflow-y-auto text-xs text-customLight">
          {props.rows.map((row, index) => (
            <li key={index} className="flex gap-3 py-0.5">
              <span className="w-12 shrink-0 text-right text-customGrey">{row.rank ?? '—'}</span>
              <span>
                {row.name}
                {row.country ? <span className="text-customGrey"> · {row.country}</span> : null}
              </span>
            </li>
          ))}
          {props.total > props.rows.length ? (
            <li className="py-1 text-customGrey">… and {props.total - props.rows.length} more</li>
          ) : null}
        </ul>
      ) : null}
    </div>
  );
};

const ImportRankingsModal = (props: ImportRankingsModalProps) => {
  const { defaultYear, onClose } = props;
  const toast = useToast();
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);

  const [year, setYear] = useState<number>(defaultYear ?? CURRENT_YEAR);
  const [file, setFile] = useState<File | null>(null);
  const [replaceExisting, setReplaceExisting] = useState(false);
  const [createMissing, setCreateMissing] = useState(true);
  const [result, setResult] = useState<UniversityRankingsImportResultDTO>();
  const [isLoading, setIsLoading] = useState(false);
  const [showFuzzy, setShowFuzzy] = useState(false);

  const send = async (mode: 'preview' | 'apply') => {
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('year', String(year));
    formData.append('mode', mode);
    formData.append('replace', String(replaceExisting));
    formData.append('createMissing', String(createMissing));
    setIsLoading(true);
    try {
      const { data } = await apiClient.post<UniversityRankingsImportResultDTO>(
        universityApis.addUniversitiesFromCSV.endpoint,
        formData
      );
      setResult(data);
      if (mode === 'apply') {
        toast.success(
          `${data.updated ?? 0} universities updated, ${data.created ?? 0} created for ${
            data.year
          }.`
        );
        queryClient.invalidateQueries(universityApis.getUniversities.endpoint);
        queryClient.invalidateQueries(universityApis.getUniversityCountriesAndRegions.endpoint);
      }
    } catch (error) {
      toast.error(errorMessage(error, 'The file could not be processed.'));
    } finally {
      setIsLoading(false);
    }
  };

  const { mutate: rollback, isLoading: isRollingBack } = useRollbackUniversityImport(
    result?.importBatchId ?? '',
    {
      onSuccess: (outcome) => {
        toast.success(
          `Rolled back: ${outcome.updatedDocs} universities reverted, ${outcome.deletedCount} removed.`
        );
        setResult(undefined);
        setFile(null);
        if (fileInput.current) fileInput.current.value = '';
      },
      onError: (error: unknown) => toast.error(errorMessage(error, 'Rollback failed.')),
    }
  );

  const reset = () => {
    setResult(undefined);
    setFile(null);
    if (fileInput.current) fileInput.current.value = '';
  };

  const applied = result?.mode === 'apply';
  const willWrite = result
    ? result.updateExact + result.updateFuzzy + (result.createMissing ? result.toCreateTotal : 0)
    : 0;

  return (
    <Modal isOpen onClose={onClose} size="large">
      <Modal.Header title="Import QS rankings" />
      <Modal.Body className="flex flex-col gap-4">
        <p className="text-sm text-customGrey">
          Upload the QS World University Rankings CSV for one edition. The file is checked first and
          nothing is written until you confirm. Universities are matched by name; ties like "7=" are
          stored as 7 and ranges like "1201-1400" as 1201.
        </p>

        {!applied ? (
          <div className="flex flex-wrap items-end gap-4">
            <div className="w-40">
              <Select
                label="Ranking year"
                variant="secondary"
                value={year}
                options={YEAR_OPTIONS}
                onSelect={(value) => {
                  setYear(Number(value));
                  setResult(undefined);
                }}
              />
            </div>
            <label className="flex flex-1 cursor-pointer flex-col gap-1 text-sm">
              <span className="text-xs font-medium text-customGrey">CSV file</span>
              <span className="rounded-lg border border-dashed border-white/20 px-4 py-2 text-customLight hover:border-customPrimary">
                {file ? file.name : 'Choose a file…'}
              </span>
              <input
                ref={fileInput}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={(event) => {
                  setFile(event.target.files?.[0] ?? null);
                  setResult(undefined);
                }}
              />
            </label>
          </div>
        ) : null}

        {!applied ? (
          <div className="flex flex-wrap gap-6 text-sm text-customLight">
            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="checkbox"
                checked={replaceExisting}
                onChange={(event) => {
                  setReplaceExisting(event.target.checked);
                  setResult(undefined);
                }}
              />
              Replace entries that already have {year}
            </label>
            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="checkbox"
                checked={createMissing}
                onChange={(event) => {
                  setCreateMissing(event.target.checked);
                  setResult(undefined);
                }}
              />
              Create universities missing from the database
            </label>
          </div>
        ) : null}

        {result ? (
          <div className="flex flex-col gap-4">
            <div className="text-sm text-white">
              {applied ? 'Imported' : 'Preview of'}{' '}
              <span className="font-semibold">{result.fileName}</span> as edition{' '}
              <span className="font-semibold">{result.year}</span>: {result.totalRows} rows,{' '}
              {result.rankedRows} with an overall rank, {result.top100} in the top 100.
            </div>
            <div className="grid grid-cols-2 gap-3 tablet:grid-cols-3">
              <Stat
                label="Update, exact name"
                value={applied ? result.updated ?? 0 : result.updateExact}
              />
              <Stat label="Update, matched by normalised name" value={result.updateFuzzy} />
              <Stat
                label={result.createMissing ? 'New universities' : 'Not in database (skipped)'}
                value={applied ? result.created ?? 0 : result.toCreateTotal}
                tone={result.toCreateTotal > 50 ? 'gold' : undefined}
              />
              <Stat
                label={
                  result.replaceExisting
                    ? `Already had ${result.year} (replaced)`
                    : `Already had ${result.year} (skipped)`
                }
                value={result.alreadyHaveYear}
                tone={result.alreadyHaveYear && !result.replaceExisting ? 'gold' : undefined}
              />
              <Stat
                label="Ambiguous (not touched)"
                value={result.ambiguous.length}
                tone={result.ambiguous.length ? 'red' : undefined}
              />
            </div>

            <div className="flex flex-col gap-1 rounded-xl bg-white/5 p-4 text-xs">
              <span className="text-sm font-semibold text-white">Top 10 in the file</span>
              {result.topTen.map((row, index) => (
                <span key={index} className="text-customLight">
                  <span className="inline-block w-8 text-right text-customGrey">{row.rank}</span>{' '}
                  {row.name}
                  <span className="text-customGrey">
                    {row.country ? ` · ${row.country}` : ''}
                    {row.score !== null && row.score !== undefined ? ` · ${row.score}` : ''}
                  </span>
                </span>
              ))}
            </div>

            {result.fuzzyMatches.length ? (
              <div className="flex flex-col gap-2 rounded-xl bg-white/5 p-4">
                <button
                  type="button"
                  className="text-left text-sm font-semibold text-white"
                  onClick={() => setShowFuzzy((value) => !value)}
                >
                  {showFuzzy ? '▾' : '▸'} Matched by normalised name ({result.updateFuzzy})
                </button>
                {showFuzzy ? (
                  <ul className="max-h-64 overflow-y-auto text-xs text-customLight">
                    {result.fuzzyMatches.map((match, index) => (
                      <li key={index} className="py-0.5">
                        <span className="text-customGrey">{match.rank ?? '—'}</span> {match.csvName}{' '}
                        <span className="text-customGrey">→</span> {match.dbName}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ) : null}

            {result.ambiguous.length ? (
              <div className="flex flex-col gap-1 rounded-xl bg-customRed/10 p-4 text-xs">
                <span className="text-sm font-semibold text-customRed">
                  Ambiguous names, left untouched. Fix them by hand after the import.
                </span>
                {result.ambiguous.map((item, index) => (
                  <span key={index} className="text-customLight">
                    {item.csvName}{' '}
                    <span className="text-customGrey">~ {item.dbNames.join(' | ')}</span>
                  </span>
                ))}
              </div>
            ) : null}

            <RowList
              title={result.createMissing ? 'Will be created' : 'Not in the database'}
              rows={result.toCreate}
              total={result.toCreateTotal}
            />

            {applied && result.importBatchId ? (
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-customGreen/10 p-4 text-sm">
                <span className="text-customGreen">
                  Done. Batch <span className="font-mono">{result.importBatchId}</span>.
                </span>
                <Button
                  variant="secondary"
                  buttonText="Roll back this import"
                  isLoading={isRollingBack}
                  disabled={isRollingBack}
                  onClick={() => rollback({})}
                />
              </div>
            ) : null}
          </div>
        ) : null}
      </Modal.Body>
      <Modal.Footer className="mt-4 flex justify-end gap-3">
        {applied ? (
          <>
            <Button variant="secondary" buttonText="Import another file" onClick={reset} />
            <Button variant="primary" buttonText="Close" onClick={onClose} />
          </>
        ) : !result ? (
          <>
            <Button variant="secondary" buttonText="Cancel" onClick={onClose} />
            <Button
              variant="primary"
              buttonText="Check file"
              isLoading={isLoading}
              disabled={!file || isLoading}
              onClick={() => send('preview')}
            />
          </>
        ) : (
          <>
            <Button variant="secondary" buttonText="Cancel" onClick={onClose} />
            <Button
              variant="primary"
              buttonText={`Import ${willWrite} universities for ${result.year}`}
              isLoading={isLoading}
              disabled={!willWrite || isLoading}
              onClick={() => send('apply')}
            />
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default ImportRankingsModal;
